import { useTranslations } from 'next-intl';
import { cn } from '@/shared/lib/cn';

/**
 * Which Pass unlocks the feature this badge sits on. The label references the
 * Pass name (Pro Pass / Designer Pass) — never a subscription tier, since
 * subscriptions are deprecated and hidden from the UI. Mirrors Apple's
 * `ProBadge.Tier` ({ pro, designer }).
 */
export type ProBadgeTier = 'pro' | 'designer';

interface ProBadgeProps {
  className?: string;
  /**
   * Whether the feature is locked for the current user. The badge is a pure
   * lock marker now: it renders **only** when `isLocked` — users who already
   * have the Pass see nothing, so unlocked rows stay clean. Callers keep
   * passing `isLocked = !hasFeature`.
   */
  isLocked?: boolean;
  /**
   * Which Pass grants the feature. Selects the brand-token label ("PRO" vs
   * "DESIGNER") and is set per feature from the fixed feature → Pass mapping
   * (EntitlementFeatureMap.php), never re-derived from runtime entitlements.
   */
  tier?: ProBadgeTier;
}

/**
 * Pass-tier pill shown next to Pass-gated options the user can't access yet.
 * Muted grey (no lock icon, no brand tint) so it reads as a quiet "locked"
 * marker without competing with the row content. Hidden entirely once the
 * feature is unlocked. "PRO"/"DESIGNER" are brand tokens kept verbatim across
 * all 24 locales.
 */
export function ProBadge({
  className,
  isLocked = true,
  tier = 'pro',
}: ProBadgeProps) {
  const t = useTranslations('passes');
  // Pure lock marker: nothing to show once the user has the feature.
  if (!isLocked) return null;
  const text = tier === 'designer' ? t('badgeDesigner') : t('badgePro');

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full bg-muted',
        'px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide',
        'leading-none text-muted-foreground whitespace-nowrap shrink-0',
        className
      )}
    >
      {text}
    </span>
  );
}
